import React from 'react'
import { Box, Typography, Tooltip, Paper } from '@mui/material'
import { ISystemUptimeEvent, ESystemStatus } from '../graphql'
import { statusColors } from '../theme'

interface ServiceUptimeTimelineProps {
  serviceName: string
  currentStatus: ESystemStatus
  historicalEvents: ISystemUptimeEvent[]
  /** Which service to filter by - checks the corresponding affected field */
  serviceKey: 'api' | 'webapp' | 'mobile'
  /** Number of days to show in the timeline */
  days?: number
}

interface DayStatus {
  date: Date
  status: ESystemStatus
  events: ISystemUptimeEvent[]
}

const getStatusColor = (status: ESystemStatus): string => {
  return statusColors[status] || statusColors.OPERATIONAL
}

const getStatusLabel = (status: ESystemStatus): string => {
  switch (status) {
    case ESystemStatus.OPERATIONAL:
      return 'Operational'
    case ESystemStatus.DEGRADED:
      return 'Degraded Performance'
    case ESystemStatus.MAINTENANCE:
      return 'Maintenance'
    case ESystemStatus.OUTAGE:
      return 'Outage'
    default:
      return 'Unknown'
  }
}

/**
 * Generates an array of day statuses for the timeline
 * Each day shows the worst status that occurred that day
 */
const generateDayStatuses = (
  historicalEvents: ISystemUptimeEvent[],
  currentStatus: ESystemStatus,
  serviceKey: 'api' | 'webapp' | 'mobile',
  days: number
): DayStatus[] => {
  const result: DayStatus[] = []
  const now = new Date()

  // Status priority (higher = worse)
  const statusPriority: Record<ESystemStatus, number> = {
    [ESystemStatus.OPERATIONAL]: 0,
    [ESystemStatus.MAINTENANCE]: 1,
    [ESystemStatus.DEGRADED]: 2,
    [ESystemStatus.OUTAGE]: 3,
  }

  // Filter events that affect this service
  const affectedField = `${serviceKey}Affected` as keyof ISystemUptimeEvent
  const relevantEvents = historicalEvents.filter((event) => {
    // If the field is explicitly false, this service wasn't affected
    // If it's true or undefined (old data), assume it was affected
    return event[affectedField] !== false
  })

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(now)
    date.setDate(date.getDate() - i)
    date.setHours(0, 0, 0, 0)

    const nextDate = new Date(date)
    nextDate.setDate(nextDate.getDate() + 1)

    // Find events that overlap with this day
    const dayEvents = relevantEvents.filter((event) => {
      const eventStart = new Date(event.startDatetime)
      const eventEnd = event.expectedConclusion ? new Date(event.expectedConclusion) : new Date() // If no end, assume ongoing

      // Event overlaps with day if it starts before day ends and ends after day starts
      return eventStart < nextDate && eventEnd >= date
    })

    // Determine worst status for the day
    let worstStatus = ESystemStatus.OPERATIONAL

    // For today, consider current status
    if (i === 0) {
      worstStatus = currentStatus
    }

    // Check historical events for this day
    for (const event of dayEvents) {
      if (statusPriority[event.status] > statusPriority[worstStatus]) {
        worstStatus = event.status
      }
    }

    result.push({
      date,
      status: worstStatus,
      events: dayEvents,
    })
  }

  return result
}

/**
 * Calculate uptime percentage based on historical events
 */
const calculateUptimePercentage = (dayStatuses: DayStatus[]): number => {
  if (dayStatuses.length === 0) return 100

  const operationalDays = dayStatuses.filter(
    (day) => day.status === ESystemStatus.OPERATIONAL
  ).length

  return Math.round((operationalDays / dayStatuses.length) * 10000) / 100
}

export const ServiceUptimeTimeline: React.FC<ServiceUptimeTimelineProps> = ({
  serviceName,
  currentStatus,
  historicalEvents,
  serviceKey,
  days = 90,
}) => {
  const dayStatuses = generateDayStatuses(historicalEvents, currentStatus, serviceKey, days)
  const uptimePercentage = calculateUptimePercentage(dayStatuses)

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {serviceName}
        </Typography>
        <Typography variant="body2" sx={{ color: getStatusColor(currentStatus), fontWeight: 500 }}>
          {getStatusLabel(currentStatus)}
        </Typography>
      </Box>

      {/* Timeline bars */}
      <Box
        sx={{
          display: 'flex',
          gap: '2px',
          mb: 1,
        }}
      >
        {dayStatuses.map((day, index) => (
          <Tooltip
            key={index}
            title={
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {day.date.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </Typography>
                <Typography variant="body2">{getStatusLabel(day.status)}</Typography>
                {day.events.length > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    {day.events.length} event{day.events.length !== 1 ? 's' : ''}
                  </Typography>
                )}
              </Box>
            }
            arrow
            placement="top"
          >
            <Box
              sx={{
                flex: 1,
                height: 32,
                backgroundColor: getStatusColor(day.status),
                borderRadius: '2px',
                cursor: 'pointer',
                transition: 'transform 0.1s, opacity 0.1s',
                '&:hover': {
                  transform: 'scaleY(1.15)',
                  opacity: 0.85,
                },
              }}
            />
          </Tooltip>
        ))}
      </Box>

      {/* Timeline labels */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          {days} days ago
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {uptimePercentage}% uptime
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Today
        </Typography>
      </Box>
    </Paper>
  )
}
